import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ChevronRight, FileCheck2, XCircle, ExternalLink, Truck } from "lucide-react";
import LineItemsEditor from "@/components/LineItemsEditor";
import AuditTrail from "@/components/AuditTrail";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import CommercialChain from "@/components/CommercialChain";
import SendEmailDialog from "@/components/SendEmailDialog";
import { Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EMAIL_ROLES = new Set(["admin", "super_admin", "plant_head", "sales"]);

export default function PoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    const { data } = await api.get(`/purchase-orders-detail/${id}`);
    setD(data);
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !d) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;
  }

  const canAcceptToOa = d.status === "Received" && ["sales", "plant_head", "admin", "super_admin"].includes(user.role);
  const canCancel = d.status === "Received" && ["plant_head", "admin", "super_admin"].includes(user.role);

  async function cancel() {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try {
      await api.post(`/purchase-orders-detail/${id}/cancel`, { reason });
      toast.success("PO cancelled");
      setCancelDialog(false); setReason("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="po-detail-page" className="space-y-5 pb-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/purchase-orders" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Customer POs
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="font-mono text-slate-900">{d.po_number}</span>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Customer PO · Stage 3 of 4</div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900">{d.po_number}</div>
            <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
              <span className="text-slate-600">{d.customer_name} · {d.plant_name}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 font-mono text-xs">{d.po_date}</span>
              {d.quotation_number && (
                <>
                  <span className="text-slate-300">·</span>
                  <Link to={`/quotations/${d.quotation_id}`} className="text-blue-900 hover:underline text-xs font-mono">
                    ↑ {d.quotation_number}
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-3">
            <div className="flex items-center gap-3">
              <CommercialStatusBadge status={d.status} className="text-xs px-3 py-1" />
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Order Value</div>
                <div className="font-mono text-lg font-bold text-slate-900">₹{(d.order_value || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {canAcceptToOa && (
                <Button size="sm" data-testid="po-accept-btn"
                        onClick={() => nav(`/order-acceptances/new?po_id=${id}`)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <FileCheck2 className="w-3.5 h-3.5 mr-1" /> Create Order Acceptance
                </Button>
              )}
              {EMAIL_ROLES.has(user?.role) && (
                <Button size="sm" variant="outline" data-testid="po-send-email-btn"
                        onClick={() => setEmailDialog(true)}>
                  <Mail className="w-3.5 h-3.5 mr-1" /> Send Email
                </Button>
              )}
              {canCancel && (
                <Button size="sm" variant="outline" data-testid="po-cancel-btn"
                        onClick={() => setCancelDialog(true)}
                        className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel PO
                </Button>
              )}
              <AdminDeleteButton
                endpoint={`/purchase-orders/${id}`}
                entityName="Customer PO"
                entityNumber={d.po_number}
                redirectAfter="/purchase-orders"
              />
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Commercial Chain</div>
        <CommercialChain type="po" entity={d} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-slate-200 lg:col-span-2 p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Line Items</div>
          <LineItemsEditor value={d.line_items || []} onChange={() => {}} readOnly showPrice />
          {d.remarks && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Remarks</div>
              <p className="text-xs text-slate-700 whitespace-pre-line">{d.remarks}</p>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card className="border-slate-200 p-5">
            <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              <Truck className="w-4 h-4 text-slate-500" />
              Delivery Schedule
            </div>
            {(d.delivery_schedule || []).length === 0 ? (
              <div className="text-xs text-slate-400">No delivery schedule defined.</div>
            ) : (
              <div className="space-y-2">
                {d.delivery_schedule.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-100">
                    <div>
                      <div className="font-mono text-xs font-semibold">{s.date}</div>
                      {s.notes && <div className="text-[11px] text-slate-500">{s.notes}</div>}
                    </div>
                    <span className="font-mono text-sm font-bold text-slate-900">{s.quantity} units</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {d.order_acceptances?.length > 0 && (
            <Card className="border-slate-200 p-5">
              <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Linked Order Acceptances</div>
              {d.order_acceptances.map((oa) => (
                <Link key={oa.id} to={`/order-acceptances/${oa.id}`}
                      className="flex items-center justify-between p-2 border border-slate-200 rounded hover:border-blue-400 hover:bg-blue-50/40">
                  <div>
                    <div className="font-mono text-xs font-semibold text-blue-900">{oa.oa_number}</div>
                    <div className="text-[11px] text-slate-500">{oa.project_name || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CommercialStatusBadge status={oa.status} />
                    <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </Link>
              ))}
            </Card>
          )}
        </div>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Audit Trail</div>
        <AuditTrail items={d.audit_trail || []} />
      </Card>

      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent data-testid="po-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Customer PO</DialogTitle>
            <DialogDescription>Cancellation is recorded in the audit trail; this cannot be undone.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Reason *</Label>
            <Textarea data-testid="po-cancel-reason" value={reason} rows={3}
                      onChange={(e) => setReason(e.target.value)} className="mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>Back</Button>
            <Button data-testid="po-cancel-confirm" onClick={cancel} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEmailDialog
        open={emailDialog} onClose={() => setEmailDialog(false)}
        defaultSubject={d ? `Order Confirmation — Customer PO ${d.po_number}` : ""}
        defaultBody={d ? `Dear Sir/Madam,\n\nWe acknowledge receipt of your Purchase Order ${d.po_number} dated ${d.po_date}.\n\nBest regards,\nHISPL Sales Team` : ""}
        relatedModule="purchase_order" relatedRecordId={id} relatedRecordNumber={d?.po_number}
      />
    </div>
  );
}
